'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { useClipboard, useToast } from '@common/hooks';
import SvgShare2 from '@common/icon/SvgShare2';
import { cn, isDev } from '@core/utils';
import { captureException } from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { UnauthQueryOptions } from '@/api';
import { DONATION_ROUTES, ENV } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useLoginGuard } from '@/hooks/useLoginGuard';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { toKakaoExecutionParams } from '@/utils/toKakaoExecutionParams';
import { INVITE_CONFIG } from '../../constants';
interface ShareButtonsProps {
  endDate: string | Date;
}
export const ShareButtons = t0 => {
  const $ = _c(58);
  const {
    endDate
  } = t0;
  const copy = useClipboard();
  const currentWebviewType = useDeviceInfoStore(useShallow(_temp));
  const userMemberCode = useAuthStore(_temp2);
  let t1;
  if ($[0] !== userMemberCode) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/home-donation-event-history/member-name"]({
      query: {
        memberCode: userMemberCode as string
      }
    });
    $[0] = userMemberCode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!userMemberCode;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2,
      select: _temp3
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: name,
    isLoading: isNameLoading
  } = useQuery(t3);
  const {
    isWelloApp
  } = useCheckWelloApp();
  const isKbPay = currentWebviewType === WEBVIEW_TYPE.KB_PAY;
  const {
    isInitialized: isKakaoSdkInitialized
  } = useKakaoShare();
  const router = useCustomRouter();
  const loginGuard = useLoginGuard();
  const {
    toast
  } = useToast();
  let t4;
  if ($[5] !== userMemberCode) {
    t4 = generateShareLinkUrl(userMemberCode);
    $[5] = userMemberCode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const inviteShareUrl = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    request: checkKakaoInstallRequest,
    response: kakaoInstallationResponse
  } = useHandleApp(t5);
  let t6;
  let t7;
  if ($[8] !== checkKakaoInstallRequest) {
    t6 = () => {
      checkKakaoInstallRequest();
    };
    t7 = [checkKakaoInstallRequest];
    $[8] = checkKakaoInstallRequest;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      type: MESSAGE_KEYWORD.SHARE_CONTENT
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    request: requestNativeShare,
    isPending: isNativeSharePending
  } = useHandleApp(t8);
  let t9;
  if ($[12] !== router) {
    t9 = () => {
      const benefitsPageUrl = DONATION_ROUTES.GUIDE.withSearchParams({
        eventParams: {
          utm_source: "wello",
          utm_medium: "invite",
          utm_campaign: "cta",
          utm_content: "button"
        }
      });
      router.push(benefitsPageUrl);
    };
    $[12] = router;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const navigateToMyBenefits = t9;
  let initiateKakaoShare;
  if ($[14] !== inviteShareUrl || $[15] !== isNativeSharePending || $[16] !== isWelloApp || $[17] !== kakaoInstallationResponse?.isSuccess || $[18] !== loginGuard || $[19] !== name || $[20] !== requestNativeShare || $[21] !== toast) {
    initiateKakaoShare = async () => {
      if (!loginGuard()) {
        return;
      }
      if (isWelloApp) {
        await executeNativeKakaoShare();
      } else {
        executeWebKakaoShare();
      }
    };
    const executeNativeKakaoShare = async () => {
      if (isNativeSharePending) {
        return;
      }
      const isKakaoInstalled = kakaoInstallationResponse?.isSuccess;
      if (!inviteShareUrl) {
        toast({
          message: "\uACF5\uC720 URL\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
          type: "fail"
        });
        return;
      }
      if (isKakaoInstalled) {
        requestNativeShare({
          share_type: "kakao",
          url: inviteShareUrl,
          thumbnail: INVITE_CONFIG.share.thumbnailImageUrl ?? "",
          subject: INVITE_CONFIG.share.title,
          description: INVITE_CONFIG.share.description
        });
      } else {
        toast({
          message: "\uCE74\uCE74\uC624\uD1A1\uC774 \uC124\uCE58\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC544\uC694",
          type: "fail"
        });
      }
    };
    const executeWebKakaoShare = () => {
      if (!inviteShareUrl) {
        return;
      }
      const executionParams = toKakaoExecutionParams(inviteShareUrl);
      window.Kakao?.Share?.sendDefault?.({
        objectType: "feed",
        content: {
          title: `${name}님이 134,000원을 선물했어요!🎁`,
          description: "\uCE5C\uAD6C \uCD08\uB300\uC7A5\uC73C\uB85C \uAC00\uC785\uD574\uC57C 134,000\uC6D0\uC744 \uBC1B\uC744 \uC218 \uC788\uC5B4\uC694.",
          imageUrl: "https://cdn.welfarehello.com/donation/event/invite/donation-invite-og-new-2.jpg",
          imageWidth: 1600,
          imageHeight: 800,
          link: {
            mobileWebUrl: inviteShareUrl,
            webUrl: inviteShareUrl,
            androidExecutionParams: executionParams,
            iosExecutionParams: executionParams
          }
        },
        buttons: [{
          title: "\uC9C0\uAE08 \uBC14\uB85C \uC120\uBB3C\uBC1B\uAE30",
          link: {
            mobileWebUrl: inviteShareUrl,
            webUrl: inviteShareUrl,
            androidExecutionParams: executionParams,
            iosExecutionParams: executionParams
          }
        }]
      });
    };
    $[14] = inviteShareUrl;
    $[15] = isNativeSharePending;
    $[16] = isWelloApp;
    $[17] = kakaoInstallationResponse?.isSuccess;
    $[18] = loginGuard;
    $[19] = name;
    $[20] = requestNativeShare;
    $[21] = toast;
    $[22] = initiateKakaoShare;
  } else {
    initiateKakaoShare = $[22];
  }
  const shouldShowKakaoShareButton = !isKbPay;
  let isDeadlinePassed;
  let isKakaoShareBlocked;
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[23] !== endDate || $[24] !== isKakaoSdkInitialized || $[25] !== isNameLoading || $[26] !== navigateToMyBenefits) {
    isDeadlinePassed = dayjs().isAfter(dayjs(endDate));
    isKakaoShareBlocked = !isKakaoSdkInitialized || isNameLoading;
    t13 = "flex items-center gap-8 px-20 py-12";
    t10 = "button";
    t11 = navigateToMyBenefits;
    t12 = cn("flex h-48 flex-1 items-center justify-center gap-8 rounded-xsmall bg-B-50 py-10 font-700 transition duration-200 typo-body3", !isDeadlinePassed && "hover:bg-B-50/80", isDeadlinePassed && "cursor-not-allowed opacity-50");
    $[23] = endDate;
    $[24] = isKakaoSdkInitialized;
    $[25] = isNameLoading;
    $[26] = navigateToMyBenefits;
    $[27] = isDeadlinePassed;
    $[28] = isKakaoShareBlocked;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    isDeadlinePassed = $[27];
    isKakaoShareBlocked = $[28];
    t10 = $[29];
    t11 = $[30];
    t12 = $[31];
    t13 = $[32];
  }
  let t14;
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <GiftIcon />;
    t15 = <span>나의 혜택받기</span>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t14 = $[33];
    t15 = $[34];
  }
  let t16;
  if ($[35] !== isDeadlinePassed || $[36] !== t10 || $[37] !== t11 || $[38] !== t12) {
    t16 = <button type={t10} onClick={t11} className={t12} disabled={isDeadlinePassed}>{t14}{t15}</button>;
    $[35] = isDeadlinePassed;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== initiateKakaoShare || $[41] !== isDeadlinePassed || $[42] !== isKakaoShareBlocked || $[43] !== shouldShowKakaoShareButton) {
    t17 = shouldShowKakaoShareButton && <button type="button" onClick={initiateKakaoShare} className={cn("flex h-48 flex-1 items-center justify-center gap-8 rounded-xsmall bg-[#FFDE32] py-10 font-700 transition duration-200 typo-body3", !isDeadlinePassed && !isKakaoShareBlocked && "hover:bg-Y-600", (isDeadlinePassed || isKakaoShareBlocked) && "cursor-not-allowed opacity-50")} disabled={isKakaoShareBlocked || isDeadlinePassed}><KakaoIcon /><span>친구 초대하기</span></button>;
    $[40] = initiateKakaoShare;
    $[41] = isDeadlinePassed;
    $[42] = isKakaoShareBlocked;
    $[43] = shouldShowKakaoShareButton;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== copy || $[46] !== inviteShareUrl || $[47] !== loginGuard) {
    t18 = () => {
      if (!loginGuard()) {
        return;
      }
      ;
      try {
        if (inviteShareUrl) {
          return copy({
            value: inviteShareUrl
          });
        }
      } catch (t19) {
        const error = t19;
        captureException(error);
      }
    };
    $[45] = copy;
    $[46] = inviteShareUrl;
    $[47] = loginGuard;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <SvgShare2 className="size-20 text-Gray-white" />;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== isDeadlinePassed || $[51] !== t18) {
    t20 = <Button className="-mx-8" theme="sub-text" dimensions="H40" disabled={isDeadlinePassed} onClick={t18}>{t19}</Button>;
    $[50] = isDeadlinePassed;
    $[51] = t18;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t13 || $[54] !== t16 || $[55] !== t17 || $[56] !== t20) {
    t21 = <div className={t13}>{t16}{t17}{t20}</div>;
    $[53] = t13;
    $[54] = t16;
    $[55] = t17;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  return t21;
};
const generateShareLinkUrl = (memberCode?: string) => {
  const targetPathname = memberCode ? DONATION_ROUTES.DONATION_INVITE_RECEIVER.pathname({
    memberCode
  }) : DONATION_ROUTES.HOME.pathname;
  let baseDomain = ENV.NEXT_PUBLIC_DOMAIN;
  if (!isDev) {
    baseDomain = baseDomain.replace('https://', 'https://www.');
  }
  const fullShareUrl = new URL(targetPathname, baseDomain);
  return fullShareUrl.href;
};
const GiftIcon = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <g clipPath="url(#clip0_16870_3212)"><path d="M14.93 14.69V7.17H1.57V14.69C1.57 14.99 1.69 15.29 1.9 15.5C2.12 15.71 2.41 15.83 2.71 15.83H13.79C14.09 15.83 14.38 15.71 14.6 15.5C14.81 15.29 14.93 14.99 14.93 14.69Z" fill="white" /><path fillRule="evenodd" clipRule="evenodd" d="M7.06 3.88C6.94 3.65 6.81 3.429 6.67 3.22C6.17 2.53 5.59 2.15 5.02 2.15C4.39 2.15 4.14 2.33 4.03 2.44C3.92 2.57 3.85 2.77 3.87 3.06C3.9 3.36 4.02 3.66 4.19 3.88H7.06ZM2.3 3.88C2.23 3.66 2.19 3.44 2.17 3.21C2.11 2.58 2.25 1.86 2.76 1.29C3.28 0.72 4.06 0.43 5.02 0.43C6.39 0.43 7.42 1.34 8.06 2.22C8.12 2.31 8.19 2.41 8.25 2.51C8.31 2.41 8.38 2.31 8.44 2.22C9.08 1.34 10.11 0.43 11.48 0.43C12.44 0.43 13.22 0.72 13.74 1.29C14.25 1.86 14.39 2.58 14.33 3.21C14.31 3.44 14.27 3.66 14.2 3.88H14.88C15.18 3.88 15.47 4.0 15.69 4.21C15.902 4.43 16.02 4.72 16.02 5.02V6.03C16.02 6.33 15.902 6.62 15.69 6.83C15.47 7.05 15.18 7.17 14.88 7.17H8.96V15.83H7.54V7.17H1.62C1.32 7.17 1.03 7.05 0.81 6.83C0.6 6.62 0.48 6.33 0.48 6.03V5.02C0.48 4.72 0.6 4.42 0.81 4.21C1.03 4.0 1.32 3.88 1.62 3.88H2.3ZM12.31 3.88C12.487 3.64 12.6 3.36 12.62 3.06C12.65 2.77 12.58 2.57 12.47 2.44C12.36 2.33 12.1 2.15 11.48 2.15C10.91 2.15 10.33 2.54 9.83 3.22C9.68 3.44 9.55 3.66 9.44 3.88H12.31Z" fill="#33A6FF" /></g>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg width="17" height="16" viewBox="0 0 17 16" fill="none">{t0}<defs><clipPath id="clip0_16870_3212"><rect width="16" height="16" fill="white" transform="translate(0.25)" /></clipPath></defs></svg>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const KakaoIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5.677 20.813C5.498 20.707 5.527 20.692 6.57 17.113C3.827 15.766 2 13.366 2 10.628C2 6.416 6.328 3 11.662 3H11.739C17.099 3 21.446 6.412 21.446 10.623C21.446 14.456 17.846 17.629 13.146 18.165C12.1 18.29 11.04 18.28 10 18.141C6.766 20.408 6.071 20.841 5.8 20.841C5.76 20.84 5.71 20.83 5.677 20.813Z" fill="#1A1A1A" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(state) {
  return state.webviewType;
}
function _temp2(state_0) {
  return state_0.myInfo?.memberCode;
}
function _temp3(t0) {
  const {
    context
  } = t0;
  return context?.name;
}